import { Button, Col, Form, Input, Row, Switch } from "antd";
import { useGetData } from "../../../../Network/useGetQueryData";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../../../components/Loading";
import { SearchIcon } from "../../../../Icons/SearchIcon";
import { ExcelUploader } from "../../../../components/UploadFiles";
import { usePostData } from "../../../../Network/usePostData";
import { PriceCard } from "./PriceCard";
import { useTranslation } from "react-i18next";

export const PricesStep = ({
  onChangeStep,
}: {
  onChangeStep?: (step: number) => void;
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>();
  const [pricesSearchKey, setPricesSearchKey] = useState<any>();
  const [editView, setEditView] = useState<boolean>();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const {
    data: priceLists,
    isLoading,
    refetch,
  } = useGetData<{
    data: any;
  }>({
    queryKey: ["service-provider-prices"],
    url: `service-provider-services?serviceProviderId=${id}&offset=0&limit=30&withDeleted=false`,
    params: { searchKey: pricesSearchKey },
    options: {
      enabled: !!id,
      queryKey: [
        "service-provider-prices",
        { branchSearchKey: pricesSearchKey },
      ],
    },
  });

  const { data, refetch: priceRefetch } = useGetData<{
    data: any;
  }>({
    queryKey: ["service-provider-uploaded"],
    url: `service-provider-services/uploaded-files?serviceProviderId=${id}`,
    params: { searchKey: pricesSearchKey },
    options: {
      enabled: !!id,
      queryKey: [
        "service-provider-uploaded",
        { branchSearchKey: pricesSearchKey },
      ],
    },
  });

  const { mutateAsync } = usePostData({
    url: "service-provider-services/upload-items",
    options: {
      onSuccess: (data) => {
        form.resetFields();
      },
      onError: (error) => {},
    },
  });

  const handleAddPrice = async (values: any) => {
    setLoading(true);
    const keys = ["name", "code", "price", "medHubPrice", "status"];

    const keysMapping = keys.reduce((acc, key) => {
      if (values[key] !== undefined) {
        acc[key] = values[key];
      }
      return acc;
    }, {});

    try {
      const body = new FormData();
      body.append("file", values.file);
      body.append("serviceProviderId", id);
      body.append("keysMapping", JSON.stringify(keysMapping));

      await mutateAsync(body);
      refetch();
      priceRefetch();

      setLoading(false);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const listData = data?.data?.changes?.length
    ? data?.data?.changes
    : priceLists?.data?.items;

  return (
    <>
      {editView && (
        <Form form={form} layout="vertical" onFinish={handleAddPrice}>
          <p className="form-section-title">{t("form.accountDetails")}</p>
          <Row gutter={32}>
            <Col xs={24} lg={24} xl={12}>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t("validation.required"),
                  },
                  {
                    max: 255,
                    message: t("validation.max255Name"),
                  },
                ]}
                label={t("form.priceListName")}
                name="fileName"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24} xl={12}>
              <Form.Item label={t("form.priceActive")} name="status">
                <Switch defaultChecked />
              </Form.Item>
            </Col>

            <Col xs={24} lg={24} xl={12}>
              <Form.Item label={t("form.uploadFile")} name="file">
                <ExcelUploader
                  onChange={(file) => {
                    form.setFieldsValue({
                      file,
                      fileName: file.name,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <p className="form-section-title">{t("form.priceListMapping")}</p>
          <Row gutter={32}>
            <Col xs={24} lg={24} xl={12}>
              <Form.Item label={t("form.serviceName")} name="name">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24} xl={12}>
              <Form.Item label={t("form.code")} name="code">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24} xl={12}>
              <Form.Item label={t("form.price")} name="price">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24} xl={12}>
              <Form.Item label={t("form.medHubPrice")} name="medHubPrice">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24} xl={12}>
              <Form.Item label={t("form.status")} name="status">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <div className="steps-action-footer">
            <Button loading={loading} type="primary" htmlType="submit">
              {t("form.save")}
            </Button>
          </div>
        </Form>
      )}

      <div className="branches-list">
        <div className="form-list-header">
          <p>{t("form.lists")}</p>

          <div className="header-search">
            {!editView && (
              <Button onClick={() => setEditView(true)} type="primary">
                {t("form.add")}
              </Button>
            )}
            <Input
              size="large"
              placeholder={t("form.searchBranches")}
              prefix={<SearchIcon />}
              onChange={(e) => setPricesSearchKey(e.target.value)}
            />
          </div>
        </div>
        <Row gutter={32}>
          {isLoading ? (
            <Loading />
          ) : !listData?.length ? (
            <div
              style={{ textAlign: "center", margin: "20px 0", width: " 100%" }}
            >
              {t("form.noLists")}
            </div>
          ) : (
            listData?.map((price) => (
              <Col key={price.id as number} xs={24} lg={24} xl={12}>
                <PriceCard
                  data={price}
                  onCardClick={() =>
                    navigate(`/providers/${id}/list/${price?.id}`)
                  }
                />
              </Col>
            ))
          )}
        </Row>
      </div>
    </>
  );
};
